import {match, Pattern} from 'ts-pattern';

import {CSSProperties, ForwardedRef, forwardRef, VideoHTMLAttributes, useRef} from 'react';

import {isNotNil} from 'ramda';

import {RequiredTestIdProps, useComposedRef, useDidUpdate, useOnMount} from 'shared/web';

import {useIsInViewport} from '../../hooks/useIsInViewport';
import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';
import {ImageFit} from '../Image/Image';

export type VideoProps = {
  volume?: number;
  width?: Integer | CSSDimension | ValueByDevice<Integer> | ValueByDevice<CSSDimension>;
  height?: Integer | CSSDimension | ValueByDevice<Integer> | ValueByDevice<CSSDimension>;
  minWidth?: Integer | CSSDimension | ValueByDevice<Integer> | ValueByDevice<CSSDimension>;
  minHeight?: Integer | CSSDimension | ValueByDevice<Integer> | ValueByDevice<CSSDimension>;
  maxWidth?: Integer | CSSDimension | ValueByDevice<Integer> | ValueByDevice<CSSDimension>;
  maxHeight?: Integer | CSSDimension | ValueByDevice<Integer> | ValueByDevice<CSSDimension>;
  fit?: ImageFit;
  ratio?: CSSProperties['aspectRatio'];
  position?: CSSProperties['objectPosition'];
  startAt?: number;
  shouldHideControls?: boolean;
} & Pick<
  VideoHTMLAttributes<HTMLVideoElement>,
  | 'src'
  | 'muted'
  | 'autoPlay'
  | 'onPause'
  | 'onPlay'
  | 'onVolumeChange'
  | 'loop'
  | 'playsInline'
  | 'preload'
> &
  RequiredTestIdProps;

function syncPlaybackWithVisibility(
  video: HTMLVideoElement | null,
  isInView: boolean,
  autoPlay: boolean | undefined
) {
  match({video, isInView, autoPlay})
    .with({video: Pattern.nullish}, () => undefined)
    .with({video: Pattern.nonNullable, isInView: false}, ({video}) => video.pause())
    .with({video: Pattern.nonNullable, autoPlay: true}, ({video}) => {
      const playPromise = video.play();
      if (typeof playPromise?.catch === 'function') {
        // browser refused to play the video, so we ignore the error
        playPromise.catch(() => {});
      }
    })
    .otherwise(() => undefined);
}

function syncVolume(video: HTMLVideoElement | null, volume: number | undefined) {
  if (isNotNil(video) && isNotNil(volume)) {
    video.volume = volume;
  }
}

function VideoComponent(props: VideoProps, ref: ForwardedRef<HTMLVideoElement>) {
  const innerRef = useRef<HTMLVideoElement>(null);
  const composedRef = useComposedRef(innerRef, ref);
  const isInView = useIsInViewport(innerRef, 0.7);

  useOnMount(() => syncPlaybackWithVisibility(innerRef.current, isInView, props.autoPlay));
  useDidUpdate(
    () => syncPlaybackWithVisibility(innerRef.current, isInView, props.autoPlay),
    [isInView, props.autoPlay]
  );

  useOnMount(() => syncVolume(innerRef.current, props.volume));
  useDidUpdate(() => syncVolume(innerRef.current, props.volume), [props.volume]);

  const width = useResponsivePropValue(props.width);
  const height = useResponsivePropValue(props.height);
  const minWidth = useResponsivePropValue(props.minWidth);
  const minHeight = useResponsivePropValue(props.minHeight);
  const maxWidth = useResponsivePropValue(props.maxWidth);
  const maxHeight = useResponsivePropValue(props.maxHeight);

  return (
    <video
      ref={composedRef}
      style={{
        width: getCssSize(width) ?? '100%',
        height: getCssSize(height) ?? '100%',
        minWidth: getCssSize(minWidth) ?? 'initial',
        minHeight: getCssSize(minHeight) ?? 'initial',
        maxWidth: getCssSize(maxWidth) ?? 'initial',
        maxHeight: getCssSize(maxHeight) ?? 'initial',
        objectFit: props.fit,
        objectPosition: props.position,
        aspectRatio: props.ratio,
      }}
      src={props.startAt ? `${props.src}#t=${props.startAt}` : props.src}
      muted={props.muted}
      playsInline={props.playsInline}
      preload={props.preload}
      autoPlay={props.autoPlay}
      controls={!props.shouldHideControls}
      controlsList="nodownload noremoteplayback noplaybackrate"
      disablePictureInPicture
      onPause={props.onPause}
      onPlay={props.onPlay}
      onVolumeChange={props.onVolumeChange}
      loop={props.loop}
    />
  );
}

export const Video = forwardRef(VideoComponent);
